import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const contactCards = [
  { icon: "fa-map-marker-alt", title: "Location", text: "Lakeland, Florida", sub: "Serving Nationwide & Internationally" },
  { icon: "fa-phone", title: "Phone", text: "(863) 585-1412", href: "tel:8635851412" },
  { icon: "fa-envelope", title: "Email", text: "info@financialfreedomdelivered.com", href: "mailto:info@financialfreedomdelivered.com" },
  { icon: "fa-globe", title: "Website", text: "financialfreedomdelivered.com", href: "https://www.financialfreedomdelivered.com" },
  { icon: "fa-clock", title: "Business Hours", text: "Mon-Fri: 9AM-5PM ET", sub: "Sat: By Appointment" },
];

const processSteps = [
  { num: "1", title: "Send Your Message", desc: "Fill out the form with your details and questions." },
  { num: "2", title: "We Receive & Review", desc: "A Paradigm advisor reviews your inquiry within 24 hours." },
  { num: "3", title: "Personal Response", desc: "We respond with personalized guidance and next steps." },
  { num: "4", title: "Strategy Session", desc: "If needed, we schedule a deeper strategy conversation." },
];

export const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setSubmitted(true);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      {/* PAGE HERO */}
      <div className="pg-page-hero">
        <div className="pg-container">
          <span className="pg-label">Get In Touch</span>
          <h1>Let's Connect</h1>
          <p>We're here to help your business thrive.</p>
        </div>
      </div>

      {/* CONTACT CARDS */}
      <section className="pg-section">
        <div className="pg-container">
          <div className="pg-contact-cards">
            {contactCards.map((c, i) => (
              <div className="pg-contact-card" key={c.title} data-testid={`contact-card-${i}`}>
                <i className={`fas ${c.icon}`} />
                <h4>{c.title}</h4>
                {c.href ? (
                  <p><a href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">{c.text}</a></p>
                ) : (
                  <p>{c.text}</p>
                )}
                {c.sub && <p style={{ fontSize: ".78rem", marginTop: ".2rem" }}>{c.sub}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section className="pg-section pg-section-light">
        <div className="pg-container">
          <div className="pg-text-center">
            <span className="pg-label">Send Us a Message</span>
            <h2>Have a Question?</h2>
            <div className="pg-divider" />
          </div>

          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            {submitted ? (
              <div data-testid="contact-success" style={{ textAlign: "center", padding: "3rem 1rem" }}>
                <div style={{
                  width: 70, height: 70, borderRadius: "50%", background: "var(--grad)",
                  display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem"
                }}>
                  <i className="fas fa-check" style={{ color: "#fff", fontSize: "1.5rem" }} />
                </div>
                <h3>Message Sent!</h3>
                <p style={{ color: "var(--muted)" }}>Thank you for reaching out. A Paradigm advisor will respond within 24 hours.</p>
                <button onClick={() => setSubmitted(false)} className="pg-btn pg-btn-secondary" style={{ marginTop: "1rem" }} data-testid="send-another-button">
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="pg-form-row">
                  <div className="pg-form-group">
                    <label>First Name *</label>
                    <input type="text" required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Your full name" data-testid="contact-name-input" />
                  </div>
                  <div className="pg-form-group">
                    <label>Email *</label>
                    <input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="your@email.com" data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="pg-form-group">
                  <label>Phone</label>
                  <input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="(555) 123-4567" data-testid="contact-phone-input" />
                </div>
                <div className="pg-form-group">
                  <label>Message *</label>
                  <textarea rows={5} required value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder="Tell us about your business or how we can help..." data-testid="contact-message-input" />
                </div>
                <button type="submit" disabled={submitting} className="pg-btn pg-btn-primary" style={{ width: "100%", justifyContent: "center" }} data-testid="contact-submit-button">
                  {submitting ? (
                    <><i className="fas fa-spinner fa-spin" /> Sending...</>
                  ) : (
                    <><i className="fas fa-paper-plane" /> Send Message</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* PROCESS STEPS */}
      <section className="pg-section">
        <div className="pg-container">
          <div className="pg-text-center">
            <span className="pg-label">What Happens Next</span>
            <h2>Our Response Process</h2>
            <div className="pg-divider" />
          </div>
          <div className="pg-steps-grid">
            {processSteps.map((s) => (
              <div className="pg-step" key={s.title}>
                <div className="pg-step-num">{s.num}</div>
                <h4 style={{ color: "var(--black)", marginBottom: ".3rem" }}>{s.title}</h4>
                <p style={{ color: "var(--muted)", fontSize: ".88rem", margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pg-cta-banner">
        <div className="pg-container">
          <h2>Ready to Stop BEING Alone?</h2>
          <p>Book a free strategy call today and discover how Paradigm can support your business journey.</p>
          <div className="pg-cta-btns">
            <a href="tel:+18635851412" className="pg-btn pg-btn-white" data-testid="cta-call-button">
              <i className="fas fa-phone" /> Call Now
            </a>
            <Link to="/consultation" className="pg-btn pg-btn-outline-white" data-testid="cta-book-button">
              <i className="fas fa-calendar-check" /> Schedule Strategy Session
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
